import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import type * as React from "react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import {
	CHAIN_STEPS,
	GROUP_TONE,
	type NodeLinkKey,
	type PlanGroup,
	type ResolvedLink,
	layerLabel,
	nodePage,
	planPage,
	resolveNodeLink,
	resolvePage,
} from "../page-registry";
import type { PlanTab } from "../shell/plan-tabs";

/**
 * 链路总览两个页签（页面地图、运行时流程）共用的东西：七个环节的唯一清单（顺序、编号、名字、配色在这里定一次）、
 * 可点节点、类型标签、一级组头与徽标、图例。点了跳哪个页面只看页面注册表 `page-registry.json` 的 overviewNodes（只读，不在这里改映射）。
 */

export type Kind = "llm" | "code" | "mixed";

export const KIND: Record<
	Kind,
	{ label: string; tone: "info" | "neutral"; card: string; num: string }
> = {
	llm: {
		label: "大模型",
		tone: "info",
		card: "border-brand-info/40 bg-brand-info/5",
		num: "bg-brand-info",
	},
	code: {
		label: "代码",
		tone: "neutral",
		card: "border-surface-border bg-surface-card",
		num: "bg-text-secondary",
	},
	mixed: {
		label: "代码＋排序模型",
		tone: "info",
		card: "border-brand-info/40 bg-surface-card",
		num: "bg-brand-info",
	},
};

export type StageKey = "signal" | "board" | "recall" | "decide" | "execute" | "settle" | "reply";

export type Stage = {
	key: StageKey;
	no: number;
	name: string;
	kind: Kind;
	to: NodeLinkKey;
	/** 运行规则页「环节」列里的取值（规则条数、跳转筛选用） */
	ruleStage: string;
	/** 做什么 */
	what: string;
	/** 产出什么 */
	out: string;
	/** 不管什么 */
	not: string;
	/** 关键机制名（只写名字，细节在运行规则页） */
	mechs: string[];
};

/** 七个环节的名字、类型、点了跳哪页；说明文字（做什么、产出、不管、机制名）只在页面注册表一处，超 30 字审计失败 */
const STAGE_BASE: { key: StageKey; name: string; kind: Kind; to: NodeLinkKey }[] = [
	{ key: "signal", name: "信号识别", kind: "llm", to: "signal" },
	{ key: "board", name: "事项看板", kind: "code", to: "board" },
	{ key: "recall", name: "候选召回", kind: "mixed", to: "recall" },
	{ key: "decide", name: "决策 N2", kind: "llm", to: "decide" },
	{ key: "execute", name: "执行把关", kind: "code", to: "execute" },
	{ key: "settle", name: "结算", kind: "code", to: "settle" },
	{ key: "reply", name: "回复 N3", kind: "llm", to: "reply" },
];

/** 七个环节，按一轮的顺序（照 `plan_lab/runtime/session.py` 的 `_turn`）。名字与菜单名一致。 */
export const STAGES: Stage[] = STAGE_BASE.map((b, i) => {
	const def = CHAIN_STEPS.find((c) => c.key === b.key);
	return {
		...b,
		no: i + 1,
		ruleStage: def?.ruleStage ?? "",
		what: def?.what ?? "",
		out: def?.out ?? "",
		not: def?.not ?? "",
		mechs: def?.mechs ?? [],
	};
});

export const stage = (k: StageKey): Stage => STAGES.find((s) => s.key === k) as Stage;

const CIRCLED = ["①", "②", "③", "④", "⑤", "⑥", "⑦"];
export const circled = (no: number): string => CIRCLED[no - 1] ?? String(no);

// ── 可点节点 ─────────────────────────────────────────────

/**
 * 有页面 → 实线、可点，悬停橙边；没页面 → 虚线灰字、不可点，悬停看原因。
 * `look` 是节点自己的边框与底色（不传用白底灰边）；形状、内边距由 className 给。
 */
export function NodeLink({
	id,
	to,
	className,
	look,
	children,
}: {
	id: string;
	to: NodeLinkKey;
	className?: string;
	look?: string;
	children: React.ReactNode;
}) {
	return (
		<LinkBox id={id} r={resolveNodeLink(to)} className={className} look={look}>
			{children}
		</LinkBox>
	);
}

/** 直接按页面（注册表里的页）跳：运行时流程底部对照表的页面列用 */
export function PageLink({
	id,
	page,
	className,
	look,
	children,
}: {
	id: string;
	page: PlanTab;
	className?: string;
	look?: string;
	children: React.ReactNode;
}) {
	return (
		<LinkBox id={id} r={resolvePage(page)} className={className} look={look}>
			{children}
		</LinkBox>
	);
}

function LinkBox({
	id,
	r,
	className,
	look = "border-surface-border bg-surface-card",
	children,
}: {
	id: string;
	r: ResolvedLink;
	className?: string;
	look?: string;
	children: React.ReactNode;
}) {
	if (r.tab === null) {
		return (
			<span
				className={cn(
					"cursor-default border border-dashed border-surface-border bg-surface-subtle text-text-muted",
					className,
				)}
				title={r.why}
				aria-disabled="true"
				data-testid={`plan-overview-node-${id}`}
				data-lit="0"
			>
				{children}
			</span>
		);
	}
	return (
		<Link
			to="/plan-lab"
			hash={r.tab}
			title={`点击打开「${planPage(r.tab).name}」页`}
			className={cn(
				"border text-text-primary transition-colors hover:border-brand-accent",
				look,
				className,
			)}
			data-testid={`plan-overview-node-${id}`}
			data-lit="1"
			data-tab={r.tab}
		>
			{children}
		</Link>
	);
}

export function KindTag({ kind }: { kind: Kind }) {
	return <Badge tone={KIND[kind].tone}>{KIND[kind].label}</Badge>;
}

/** 环节编号圆点：大模型青色、代码灰色 */
export function StageNo({ s, className }: { s: Stage; className?: string }) {
	return (
		<span
			className={cn(
				"inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-text-inverse",
				KIND[s.kind].num,
				className,
			)}
			aria-hidden
		>
			{s.no}
		</span>
	);
}

/**
 * 环节卡：编号＋名字、类型标签，居中；整张卡可点、跳到这一步所在的页面（悬停右上角出箭头、边框变橙，悬停提示写页面名）。
 * 步骤名和页面名相同（如信号识别 → 信号识别页）就不再标页面，避免同一个词在一张卡里出现两次；
 * 不同时（如决策 N2 → 模型提示页）卡底加一行小字「页面：xxx」。
 */
export function StageCard({ s, id, className }: { s: Stage; id: string; className?: string }) {
	const pageId = nodePage(s.to);
	const pageName = pageId ? planPage(pageId).name : null;
	return (
		<NodeLink
			id={id}
			to={s.to}
			look={KIND[s.kind].card}
			className={cn(
				"group relative flex flex-col items-center justify-center gap-1.5 rounded-lg px-3 py-3 text-center",
				className,
			)}
		>
			<ArrowUpRight
				className="absolute right-1.5 top-1.5 h-3.5 w-3.5 text-brand-accent opacity-0 transition-opacity group-hover:opacity-100"
				aria-hidden
			/>
			<span className="inline-flex items-center gap-1.5">
				<StageNo s={s} />
				<span className="text-sm font-semibold">{s.name}</span>
			</span>
			<KindTag kind={s.kind} />
			{pageName && pageName !== s.name ? (
				<span className="text-xs text-text-muted" data-testid={`plan-overview-stage-page-${s.key}`}>
					页面：{pageName}
				</span>
			) : null}
		</NodeLink>
	);
}

/** 一级的头：配色竖条 ＋ 名字 ＋ 回答什么 ＋「所在层 · 来源」徽标。页面地图的组头用；运行时流程对照表用同色的 GroupTag，两处同名同色 */
export function GroupHeading({ g }: { g: PlanGroup }) {
	const t = GROUP_TONE[g.tone];
	return (
		<div
			className={cn("flex flex-wrap items-center gap-x-2 gap-y-0.5 border-l-4 pl-2", t.bar)}
			data-testid={`plan-group-heading-${g.key}`}
		>
			<span className="text-sm font-semibold text-text-primary">{g.label}</span>
			<span className="text-xs text-text-secondary">{g.answer}</span>
			<span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", t.chip)}>
				{layerLabel(g.layer)}
				{g.origin !== "—" ? ` · ${g.origin}` : ""}
			</span>
		</div>
	);
}

/** 一级的小徽标（圆点 ＋ 名字），表格行里标页面属于哪一组 */
export function GroupTag({ g }: { g: PlanGroup }) {
	const t = GROUP_TONE[g.tone];
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
				t.chip,
			)}
		>
			<span className={cn("h-1.5 w-1.5 rounded-full", t.dot)} aria-hidden />
			{g.label}
		</span>
	);
}

export function Legend() {
	return (
		<div className="flex flex-wrap items-center gap-3 text-xs text-text-muted">
			<span className="inline-flex items-center gap-1">
				<KindTag kind="llm" />
				调大模型
			</span>
			<span className="inline-flex items-center gap-1">
				<KindTag kind="code" />
				固定代码
			</span>
			<span>点卡片跳到对应页面；虚线的暂无页面</span>
		</div>
	);
}

export function BlockTitle({
	title,
	sub,
	right,
}: { title: string; sub: string; right?: React.ReactNode }) {
	return (
		<div className="flex flex-wrap items-end justify-between gap-2">
			<div className="space-y-0.5">
				<h3 className="text-sm font-semibold text-text-primary">{title}</h3>
				<p className="text-xs text-text-secondary">{sub}</p>
			</div>
			{right}
		</div>
	);
}
